import { emailValid } from "@/Utilities";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import React, { lazy, Suspense, useEffect, useRef } from "react";
import { AiOutlinePhone } from "react-icons/ai";
import { BsPersonVcard } from "react-icons/bs";
import { isValidPhoneNumber } from "react-phone-number-input";
import AppointmentModal from "../../../../../Components/AppointmentModal/AppointmentModal";
import { useAppointmentModalStore } from "../../../../../Components/AppointmentModal/useAppointmentModalStore_";
import { useCalendarStore } from "../../store/useCalendarStore";
import BreakModal from "./BreakModal";
import PrescheduledAppointmentModal from "./PrescheduledAppointmentModal";
import { ArrowLeft, ArrowRight, ReceiptText } from "lucide-react";
import { useNavigate } from "react-router";
import AppointmentActionsDropdown from "./AppointmentActionsDropdown/AppointmentActionsDropdown";
import AppointmentLog from "./AppointmentLog/AppointmentLog";
import {
  AccordionItem,
  addToast,
  Button,
  Divider,
  DrawerProps,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Snippet,
  Spinner,
  Tab,
  Tabs,
} from "@heroui/react";
import { LimeAccordion } from "@/Components/NextBase/LimeAccordion";
import { UserX } from "lucide-react";
import { cn } from "@/utils";
import { AnimatePresence, motion } from "motion/react";
import { LimeDrawer } from "@/Components/NextBase/LimeDrawer";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useSoftKeyboardOpen } from "@/hooks/useSoftKeyboardOpen";
import dayjs from "dayjs";
import { RiUserLine } from "react-icons/ri";
import { useShallow } from "zustand/react/shallow";
import { api } from "@/lib/api-client";
import {
  NotificationSettingsSwitchList,
  NotificationSwitchRow,
  renderChannelIcons,
} from "@/Components/AppointmentModal/components/NotificationSettingsSwitchList";
import { findEnabledSetting } from "@/features/notification-settings/utils/findEnabledSetting";

const CustomerPage = lazy(() =>
  import("@/pages/Dashboard/Customers/CustomerPage/Customer").then(
    (module) => ({
      default: module.CustomerPage,
    }),
  ),
);

const CalendarModalWrapper = ({
  refetchTimeoffs,
}: {
  refetchTimeoffs: () => void;
}) => {
  const navigate = useNavigate();

  const {
    newBreakData,
    setNewBreakData,
    selectedAppointmentId,
    setSelectedAppointmentId,
    hideModalTabs,
    setHideModalTabs,
    isExpandedModal,
    setIsExpandedModal,
    setNewPreScheduledAppointmentData,
    newPreScheduledAppointmentData,
    closeModals,
    calendarMode,
    selectedTimeOff,
    setSelectedTimeOff,
  } = useCalendarStore((state) => ({
    newBreakData: state.newBreakData,
    setNewBreakData: state.setNewBreakData,
    selectedTimeOff: state.selectedTimeOff,
    setSelectedTimeOff: state.setSelectedTimeOff,
    selectedAppointmentId: state.selectedAppointmentId,
    setSelectedAppointmentId: state.setSelectedAppointmentId,
    hideModalTabs: state.hideModalTabs,
    setHideModalTabs: state.setHideModalTabs,
    isExpandedModal: state.isExpandedModal,
    setIsExpandedModal: state.setIsExpandedModal,
    setNewPreScheduledAppointmentData: state.setNewPreScheduledAppointmentData,
    newPreScheduledAppointmentData: state.newPreScheduledAppointmentData,
    closeModals: state.closeModals,
    calendarMode: state.calendarMode,
  }));

  const {
    confirmedServiceSelection,
    handleConfirmServiceSelection,
    setConfirmedServiceSelection,
    selectedServices,
    wirelesslySubmitAppointmentForm,
    appointmentData,
    selectedUserAppointment,
    fetchAppointmentData,
    currentAppointmentGsm,
    currentAppointmentEmail,
    isCreatingAppointment,
    canDismissDrawer,
  } = useAppointmentModalStore((state) => ({
    confirmedServiceSelection: state.confirmedServiceSelection,
    handleConfirmServiceSelection: state.handleConfirmServiceSelection,
    setConfirmedServiceSelection: state.setConfirmedServiceSelection,
    selectedServices: state.selectedServices,
    wirelesslySubmitAppointmentForm: state.wirelesslySubmitAppointmentForm,
    appointmentData: state.appointmentData,
    selectedUserAppointment: state.selectedUserAppointment,
    fetchAppointmentData: state.fetchAppointmentData,
    currentAppointmentGsm: state.currentAppointmentGsm,
    currentAppointmentEmail: state.currentAppointmentEmail,
    isCreatingAppointment: state.isCreatingAppointment,
    canDismissDrawer: state.canDismissDrawer,
  }));

  const { isFeatureEnabled } = useClientFeature();

  // Phone keyboards leave only a strip of drawer visible, so the header
  // collapses to the title + close row while the user types.
  const isCompactHeader = useSoftKeyboardOpen();

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const isExistingAppointmentSelected =
    selectedAppointmentId != null && selectedAppointmentId !== -1;

  const [selectedTab, setSelectedTab] = React.useState(
    permissions.manage_appointments ||
      ![null, -1].includes(selectedAppointmentId)
      ? "appointment"
      : "break",
  );

  const [customerCardCustomerId, setCustomerCardCustomerId] = React.useState<
    number | undefined
  >(undefined);

  const [isBreakFormValid, setIsBreakFormValid] = React.useState(false);

  const isDrawerOpen =
    selectedAppointmentId != null ||
    newBreakData != null ||
    selectedTimeOff != null;

  const activeTabRef = useRef(null);
  const modalResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    if (!isDrawerOpen) return;

    if (selectedTimeOff != null || newBreakData?.opensTimeOffTab) {
      setSelectedTab("break");
    } else if (
      selectedAppointmentId != null &&
      permissions.manage_appointments
    ) {
      setSelectedTab("appointment");
    } else if (newBreakData != null && calendarMode !== "resources") {
      setSelectedTab("break");
    } else if (permissions.manage_appointments) {
      setSelectedTab("scheduled_appointment");
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    setSelectedTab(
      permissions.manage_appointments || isExistingAppointmentSelected
        ? "appointment"
        : "break",
    );
  }, [selectedAppointmentId]);

  let drawerTitle;
  if (selectedTab === "appointment") {
    const isNewAppointment = selectedAppointmentId === -1;

    if (isNewAppointment) {
      drawerTitle = t`Dodajanje termina`;
    } else {
      drawerTitle = t`Urejanje termina`;
    }
  } else if (selectedTab === "customer_card") {
    drawerTitle = t`Kartica stranke`;
  } else if (selectedTab === "prescheduled-customer-list") {
    drawerTitle = t`Stranke termina`;
  } else if (selectedTab === "break") {
    drawerTitle =
      selectedTimeOff != null ? t`Urejanje odsotnosti` : t`Nova odsotnost`;
  } else if (selectedTab === "appointment_log") {
    drawerTitle = t`Dnevnik sprememb`;
  } else {
    drawerTitle = t`Skupinski termin`;
  }

  const onClose = () => {
    setNewBreakData(null);
    setSelectedTimeOff(null);
    setNewPreScheduledAppointmentData(null);
    setSelectedAppointmentId(null);
    closeModals();
    refetchTimeoffs();

    if (modalResetTimeoutRef.current) {
      clearTimeout(modalResetTimeoutRef.current);
    }
    modalResetTimeoutRef.current = setTimeout(() => {
      setHideModalTabs(false);
      setIsExpandedModal(false);
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (modalResetTimeoutRef.current) {
        clearTimeout(modalResetTimeoutRef.current);
        modalResetTimeoutRef.current = null;
      }
    };
  }, []);

  const openCustomerCard = (customerId?: number) => {
    setCustomerCardCustomerId(customerId);
    setSelectedTab("customer_card");
  };

  let confirmAction: {
    text?: string;
    action?: () => void;
    disabled?: boolean;
    identifier?: string;
  } = {};
  if (selectedTab === "appointment" && permissions.manage_appointments) {
    if (isExistingAppointmentSelected) {
      if (appointmentData?.isPreScheduled) {
        if (!selectedUserAppointment) {
          confirmAction = {
            text: t`Dodaj stranko`,
            action: () => {
              wirelesslySubmitAppointmentForm();
            },
          };
        } else {
          confirmAction = {
            text: t`Posodobi podatke`,
            action: () => {
              wirelesslySubmitAppointmentForm();
            },
          };
        }
      } else {
        confirmAction = {
          text: t`Posodobi podatke`,
          action: () => {
            wirelesslySubmitAppointmentForm();
          },
        };
      }
    } else {
      if (!confirmedServiceSelection) {
        confirmAction = {
          text: t`Potrdi izbiro`,
          action: () => {
            handleConfirmServiceSelection(true);
          },
          identifier: "confirm-service-selection",
        };

        if (selectedServices.length === 0) {
          confirmAction.disabled = true;
        }
      } else {
        confirmAction = {
          text: t`Dodaj termin`,
          action: () => {
            wirelesslySubmitAppointmentForm();
          },
          identifier: "submit-appointment",
          // disabled if phone number invalid
          disabled:
            (currentAppointmentGsm &&
              !isValidPhoneNumber(currentAppointmentGsm)) ||
            (currentAppointmentEmail && !emailValid(currentAppointmentEmail)) ||
            selectedServices.length < 1 ||
            isCreatingAppointment,
        };
      }
    }
  } else if (selectedTab === "break") {
    // manj ko gledas to kodo bols je
    // zdej sm naredu sam se slabs
    confirmAction = {
      text: selectedTimeOff != null ? t`Shrani` : t`Shrani odsotnost`,
      action: () => {
        // @ts-expect-error - idk
        activeTabRef.current?.action(refetchTimeoffs);
      },
      disabled: !isBreakFormValid,
    };
  } else if (selectedTab === "scheduled_appointment") {
    confirmAction = {
      text: t`Dodaj skupinski termin`,
      action: () => {
        document.getElementById("submit-scheduled-appointment-button")?.click();
      },
      identifier: "confirm-action-button",
      disabled: !(
        newPreScheduledAppointmentData?.selectedServiceId &&
        (newPreScheduledAppointmentData?.selectedUserId ||
          newPreScheduledAppointmentData?.resources) &&
        newPreScheduledAppointmentData?.startTime &&
        newPreScheduledAppointmentData?.endTime &&
        newPreScheduledAppointmentData?.numberOfPeople
      ),
    };
  }

  const drawerSize: DrawerProps["size"] = (() => {
    if (selectedTab === "customer_card") return "full";
    if (isExpandedModal) return "5xl";
    return "lg";
  })();

  return (
    <>
      <LimeDrawer
        drawerProps={{
          isOpen: isDrawerOpen,
          onClose: onClose,
          size: drawerSize,
          placement: "right",
          shouldBlockScroll: true,
          dataIdentifier: "calendar-modal-wrapper",
          isDismissable: canDismissDrawer ?? true,
        }}
        headerProps={{
          className: "w-full pb-0",
        }}
        header={{
          closeButtonRowContent: isCompactHeader ? (
            <p className="truncate" data-identifier={"drawer-title"}>
              {drawerTitle}
            </p>
          ) : selectedAppointmentId != null && selectedAppointmentId !== -1 ? (
            <Snippet
              className="cursor-pointer text-xs text-gray-500"
              size="sm"
              symbol="#"
              hideCopyButton
              onClick={() => {
                navigator.clipboard.writeText(selectedAppointmentId.toString());
                addToast({
                  title: t`Številka termina je bila kopirana`,
                  color: "success",
                });
              }}
            >
              {selectedAppointmentId === -1 ? `` : selectedAppointmentId}
            </Snippet>
          ) : undefined,
          content: isCompactHeader ? undefined : (
            <div className="flex w-full flex-col gap-1">
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex h-10 items-center">
                    <AnimatePresence>
                      {(confirmedServiceSelection ||
                        selectedTab === "customer_card" ||
                        selectedTab === "appointment_log") && (
                        <motion.div
                          key={"back-button"}
                          initial={{ opacity: 0, scale: 0, width: 0 }}
                          animate={{ opacity: 1, scale: 1, width: "auto" }}
                          exit={{ opacity: 0, scale: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Button
                            onPress={() => {
                              if (confirmedServiceSelection) {
                                setConfirmedServiceSelection(false);
                                return;
                              }

                              setSelectedTab(
                                appointmentData?.isPreScheduled
                                  ? "prescheduled-customer-list"
                                  : "appointment",
                              );

                              fetchAppointmentData(selectedAppointmentId);
                            }}
                            isIconOnly
                            variant="light"
                            className="mr-2"
                          >
                            <ArrowLeft size={16} />
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <p data-identifier={"drawer-title"}>{drawerTitle}</p>
                  </div>
                  <div className="flex items-center">
                    {isExistingAppointmentSelected &&
                    permissions.manage_appointments &&
                    appointmentData &&
                    selectedTab !== "customer_card" ? (
                      <AppointmentActionsDropdown
                        appointment={appointmentData}
                        selectedAppointmentId={selectedAppointmentId}
                      ></AppointmentActionsDropdown>
                    ) : undefined}

                    <AnimatePresence>
                      {isExistingAppointmentSelected &&
                      permissions.manage_appointments &&
                      isFeatureEnabled("taxRegister") &&
                      selectedTab !== "customer_card" &&
                      selectedTab !== "appointment_log" ? (
                        <motion.div
                          key={"invoice-button"}
                          initial={{ opacity: 0, scale: 0, width: 0 }}
                          animate={{ opacity: 1, scale: 1, width: "auto" }}
                          exit={{ opacity: 0, scale: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Button
                            onPress={() => {
                              const uApptId = selectedUserAppointment;

                              const userId =
                                appointmentData.appointmentParts[0]?.users?.[0];

                              type Customer = NonNullable<
                                typeof appointmentData
                              >["customers"][number];

                              const customer = appointmentData.customers.find(
                                (customer: Customer) =>
                                  customer.userAppointmentId == uApptId,
                              );

                              if (!uApptId || !customer) return;

                              const existingInvoiceId = customer.invoiceId;

                              if (existingInvoiceId) {
                                // navigate to archive and open this invoice
                                closeModals();
                                navigate(
                                  `/dashboard/tax-register/invoice/${existingInvoiceId}`,
                                );
                              } else {
                                // navigate to POS page
                                closeModals();
                                navigate(
                                  `/dashboard/tax-register/invoice/new?uaid=${uApptId}${userId ? `&auid=${userId}` : ""}`,
                                );
                              }
                            }}
                            isIconOnly
                            isDisabled={selectedUserAppointment == null}
                            variant="light"
                            className="ml-2"
                          >
                            <ReceiptText />
                          </Button>
                        </motion.div>
                      ) : undefined}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
              <Tabs
                aria-label="Calendar Modal Tabs"
                selectedKey={selectedTab}
                disableAnimation
                fullWidth
                size="sm"
                onSelectionChange={(tab) => {
                  setSelectedTab(String(tab));

                  if (tab === "customer_card") {
                    openCustomerCard(
                      appointmentData?.customers?.at(0)?.customerId,
                    );
                  }
                }}
                defaultSelectedKey={
                  permissions.manage_appointments ? "appointment" : "break"
                }
                className={cn("flex max-w-118 overflow-x-scroll", {
                  hidden:
                    selectedTab === "customer_card" ||
                    // Editing a time off opened from the calendar: the drawer is
                    // about that one occurrence, so there is nothing to tab to.
                    selectedTimeOff != null ||
                    (selectedAppointmentId === -1 && confirmedServiceSelection),
                })}
              >
                {!(
                  hideModalTabs &&
                  selectedTab !== "appointment" &&
                  selectedTab !== "customer_card" &&
                  selectedTab !== "prescheduled-customer-list" &&
                  selectedTab !== "appointment_log"
                ) &&
                (permissions.manage_appointments ||
                  isExistingAppointmentSelected) ? (
                  <>
                    {permissions.manage_appointments ||
                    isExistingAppointmentSelected ? (
                      <Tab key={"appointment"} title={t`Termin`}></Tab>
                    ) : null}
                    {!appointmentData?.isPreScheduled &&
                    selectedAppointmentId != null &&
                    selectedAppointmentId !== -1 &&
                    permissions.view_customer_card_on_calendar ? (
                      <Tab
                        key={"customer_card"}
                        title={t`Kartica stranke`}
                      ></Tab>
                    ) : null}
                    {selectedAppointmentId !== -1 &&
                      isFeatureEnabled("changeLog") && (
                        <Tab key={"appointment_log"} title={t`Spremembe`}></Tab>
                      )}
                    {appointmentData?.isPreScheduled &&
                      selectedAppointmentId != null &&
                      selectedAppointmentId !== -1 && (
                        <Tab
                          key={"prescheduled-customer-list"}
                          title={t`Stranke`}
                        ></Tab>
                      )}
                  </>
                ) : null}

                {!(hideModalTabs && selectedTab !== "break") ? (
                  <Tab
                    key={"break"}
                    title={t`Odsotnost`}
                    data-identifier={"time-off-tab"}
                  ></Tab>
                ) : null}
                {!(hideModalTabs && selectedTab !== "scheduled_appointment") &&
                isFeatureEnabled("prescheduledAppointments") &&
                (permissions.manage_appointments ||
                  isExistingAppointmentSelected) ? (
                  <Tab
                    key={"scheduled_appointment"}
                    title={t`Skupinski termin`}
                    data-identifier={"prescheduled-appointment-tab"}
                  ></Tab>
                ) : null}
              </Tabs>
            </div>
          ),
        }}
        bodyProps={{
          className: cn("gap-1", {
            "p-0 md:p-1": selectedTab === "customer_card",
          }),
        }}
        body={{
          content: (
            <>
              {selectedTab === "appointment" && (
                <AppointmentModal
                  appointmentId={selectedAppointmentId}
                  openCustomerCard={openCustomerCard}
                />
              )}

              {selectedTab === "appointment_log" && (
                <AppointmentLog appointmentId={selectedAppointmentId} />
              )}

              {selectedTab === "break" && (
                <BreakModal
                  ref={activeTabRef}
                  onClose={onClose}
                  onValidChange={setIsBreakFormValid}
                />
              )}

              {selectedTab === "scheduled_appointment" && (
                <PrescheduledAppointmentModal onClose={onClose} />
              )}

              {selectedTab === "customer_card" &&
              customerCardCustomerId != null ? (
                <Suspense
                  fallback={
                    <div className="flex justify-center p-8">
                      <Spinner />
                    </div>
                  }
                >
                  <CustomerPage customerId={customerCardCustomerId} />
                </Suspense>
              ) : null}

              {selectedTab === "prescheduled-customer-list" &&
                (() => {
                  type Customer = NonNullable<
                    typeof appointmentData
                  >["customers"][number];

                  const activeCustomers =
                    appointmentData?.customers?.filter(
                      (customer: Customer) => !customer?.isDeleted,
                    ) ?? [];
                  const deletedCustomers =
                    appointmentData?.customers?.filter(
                      (customer: Customer) => customer?.isDeleted,
                    ) ?? [];

                  const renderCustomerRow = (
                    customer: Customer,
                    i: number,
                    isDeleted: boolean,
                  ) => (
                    <div
                      key={
                        customer?.userAppointmentId ?? customer?.customerId ?? i
                      }
                      className={cn(
                        "flex flex-col rounded-xl p-3",
                        i % 2 === 1 && "bg-default-100",
                        isDeleted && "opacity-60",
                      )}
                      style={{
                        fontWeight: 400,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p
                            className={cn(
                              "leading-[1.2] font-medium",
                              isDeleted && "line-through",
                            )}
                          >
                            {customer?.name} {customer?.lastName}
                          </p>
                          <p className="text-xs leading-none text-gray-500">
                            {customer?.gsm}
                          </p>
                          <p className="text-xs leading-none text-gray-500">
                            {customer?.email}
                          </p>
                        </div>
                        <div className="flex items-center">
                          <Button
                            onPress={() => {
                              openCustomerCard(customer?.customerId);
                            }}
                            size={"lg"}
                            variant="light"
                            isIconOnly
                            isDisabled={!customer?.customerId}
                          >
                            <BsPersonVcard size="50%" />
                          </Button>
                          <Divider className="mx-2" orientation="vertical" />
                          <Button
                            as={Link}
                            href={`tel:${customer?.gsm}`}
                            size={"lg"}
                            variant="light"
                            isIconOnly
                            isDisabled={!customer?.gsm}
                          >
                            <AiOutlinePhone
                              size={"50%"}
                              style={{
                                transform: "scaleX(-1)",
                              }}
                            />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div className="flex flex-col">
                      {activeCustomers.map((customer: Customer, i: number) =>
                        renderCustomerRow(customer, i, false),
                      )}

                      {deletedCustomers.length > 0 ? (
                        <div className="mt-2">
                          <LimeAccordion
                            variant="bordered"
                            itemClasses={{
                              trigger: "cursor-pointer",
                            }}
                          >
                            <AccordionItem
                              textValue={t`Odstranjene stranke`}
                              startContent={<UserX size={16} />}
                              title={
                                <div className="flex items-center gap-2">
                                  <p className="text-xs font-medium">
                                    <Trans>Odstranjene stranke</Trans>
                                  </p>
                                  <span className="text-default-500 text-xs">
                                    ({deletedCustomers.length})
                                  </span>
                                </div>
                              }
                            >
                              <div className="flex flex-col">
                                {deletedCustomers.map(
                                  (customer: Customer, i: number) =>
                                    renderCustomerRow(customer, i, true),
                                )}
                              </div>
                            </AccordionItem>
                          </LimeAccordion>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
            </>
          ),
        }}
        footer={{
          content: (
            <>
              {selectedTab !== "customer_card" && (
                <Button className="w-full" variant="bordered" onPress={onClose}>
                  <Trans>Prekliči</Trans>
                </Button>
              )}
              {selectedTab !== "customer_card" &&
              selectedTab !== "appointment_log" &&
              selectedTab !== "prescheduled-customer-list" &&
              (permissions.manage_appointments || selectedTab === "break") ? (
                <Button
                  data-identifier={"confirm-action-button"}
                  onPress={() => {
                    confirmAction.action?.();
                  }}
                  isDisabled={confirmAction.disabled}
                  data-initializer={confirmAction.identifier ?? undefined}
                  className="w-full"
                  color="primary"
                  isLoading={isCreatingAppointment}
                >
                  {confirmAction.text}
                </Button>
              ) : undefined}
            </>
          ),
        }}
      ></LimeDrawer>

      <AppointmentRescheduleModal />
      <InvalidAppointmentsModal />
    </>
  );
};

const AppointmentRescheduleModal = () => {
  const {
    rescheduleSendNotifications,
    setRescheduleSendNotifications,
    rescheduleAllLinked,
    setRescheduleAllLinked,
    rescheduleAppointmentData,
    setRescheduleAppointmentData,
    setCanDismissDrawer,
    selectedServices,
    notificationSettingToggles,
    setNotificationSettingEnabled,
    previousStartTime,
  } = useAppointmentModalStore(
    useShallow((state) => ({
      rescheduleSendNotifications: state.rescheduleSendNotifications,
      setRescheduleSendNotifications: state.setRescheduleSendNotifications,
      rescheduleAllLinked: state.rescheduleAllLinked,
      setRescheduleAllLinked: state.setRescheduleAllLinked,
      rescheduleAppointmentData: state.rescheduleAppointmentData,
      setRescheduleAppointmentData: state.setRescheduleAppointmentData,
      setCanDismissDrawer: state.setCanDismissDrawer,
      selectedServices: state.selectedServices,
      notificationSettingToggles: state.notificationSettings,
      setNotificationSettingEnabled: state.setNotificationSettingEnabled,
      previousStartTime: state.initialAppointmentData?.startTime,
    })),
  );

  const { hasRescheduleNotification } = useCalendarStore(
    useShallow((state) => ({
      hasRescheduleNotification: state.hasRescheduleNotification,
    })),
  );

  const { data: notificationSettings } =
    api.notification.useGetNotificationSettings();
  const { data: notificationChannels } =
    api.notification.useGetNotificationChannels();

  const rescheduleSetting = findEnabledSetting(notificationSettings?.settings, {
    trigger: "onReschedule",
    event: "rescheduled",
  });

  const { refreshCalendar } = useCalendarStore(
    useShallow((state) => ({
      refreshCalendar: state.refreshCalendar,
    })),
  );

  if (!rescheduleAppointmentData) return null;

  const { appointmentData } = rescheduleAppointmentData;

  if (!appointmentData) return null;

  return (
    <Modal
      isOpen={rescheduleAppointmentData != null}
      onClose={() => {
        setCanDismissDrawer(true);
        setRescheduleAppointmentData(null);
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <Trans>Prestavitev termina</Trans>
            </ModalHeader>
            <ModalBody className="gap-5">
              <div className="bg-content2 rounded-large flex items-center justify-between gap-3 p-3">
                {previousStartTime ? (
                  <>
                    <div className="flex flex-col">
                      <span className="text-default-500 text-xs">
                        {dayjs(previousStartTime).format("dd, DD.MM.YYYY")}
                      </span>
                      <span className="text-default-500 text-sm line-through">
                        {dayjs(previousStartTime).format("HH:mm")}
                      </span>
                    </div>

                    <ArrowRight className="text-default-400 size-4 shrink-0" />
                  </>
                ) : null}

                <div className="flex flex-col text-right">
                  <span className="text-default-500 text-xs">
                    {dayjs(appointmentData.startTime).format("dd, DD.MM.YYYY")}
                  </span>
                  <span className="text-sm leading-5 font-semibold">
                    {dayjs(appointmentData.startTime).format("HH:mm")}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-default-500 text-xs font-medium">
                  <Trans>Prestavljate termin za naslednje stranke</Trans>
                </span>

                <div className="flex flex-wrap gap-1.5">
                  {appointmentData.customers?.map(
                    (customer: (typeof appointmentData.customers)[number]) => (
                      <span
                        className="bg-content2 flex items-center gap-1.5 rounded-full py-1 pr-3 pl-2 text-sm"
                        key={customer.customerId}
                      >
                        <RiUserLine
                          className="text-default-500 shrink-0"
                          size={".85rem"}
                        />
                        {customer.name} {customer.lastName}
                      </span>
                    ),
                  )}
                </div>
              </div>

              {hasRescheduleNotification ||
              notificationSettingToggles.length ? (
                <div className="flex flex-col gap-2">
                  <span className="text-default-500 text-xs font-medium">
                    <Trans>Pošlji obvestila?</Trans>
                  </span>

                  <div className="flex flex-col gap-1">
                    {hasRescheduleNotification && (
                      <NotificationSwitchRow
                        label={t`Ob spremembi termina`}
                        icons={renderChannelIcons(
                          rescheduleSetting,
                          selectedServices,
                          appointmentData.locationId,
                          notificationChannels,
                        )}
                        isSelected={rescheduleSendNotifications}
                        onValueChange={setRescheduleSendNotifications}
                      />
                    )}

                    <NotificationSettingsSwitchList
                      values={notificationSettingToggles}
                      onToggle={setNotificationSettingEnabled}
                      settings={notificationSettings?.settings}
                      selectedServices={selectedServices}
                      locationId={appointmentData.locationId}
                      notificationChannels={notificationChannels}
                    />
                  </div>
                </div>
              ) : null}

              {appointmentData.linkedAppointment && (
                <div className="flex flex-col gap-2">
                  <span className="text-default-500 text-xs font-medium">
                    <Trans>Termin je povezan z ostalimi</Trans>
                  </span>

                  <LimeSwitch
                    size="sm"
                    isSelected={rescheduleAllLinked}
                    onValueChange={setRescheduleAllLinked}
                    classNames={{ label: "w-full" }}
                    isCompact
                  >
                    <span className="text-sm leading-5">
                      <Trans>Prestavi vse povezane termine?</Trans>
                    </span>
                  </LimeSwitch>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                onPress={() => {
                  onClose();
                }}
              >
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                data-identifier="confirm-reschedule-appointment"
                onPress={async () => {
                  document.getElementById("appointment-form-submit")?.click();

                  await refreshCalendar();

                  onClose();
                }}
              >
                <Trans>Prestavi</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const InvalidAppointmentsModal = () => {
  const {
    invalidAppointmentsData,
    setInvalidAppointmentsData,
    setCanDismissDrawer,
  } = useAppointmentModalStore(
    useShallow((state) => ({
      invalidAppointmentsData: state.invalidAppointmentsData,
      setInvalidAppointmentsData: state.setInvalidAppointmentsData,
      setCanDismissDrawer: state.setCanDismissDrawer,
    })),
  );

  if (!invalidAppointmentsData) return null;

  const { items, dataForPost } = invalidAppointmentsData;

  return (
    <Modal
      isOpen={invalidAppointmentsData != null}
      onClose={() => {
        setCanDismissDrawer(true);
        setInvalidAppointmentsData(null);
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <Trans>Nekateri termini niso veljavni</Trans>
            </ModalHeader>
            <ModalBody>
              <div aria-live="polite">
                {items?.map(
                  (item: { dateTime: string; isRestricted?: boolean }) => (
                    <div
                      className={cn("flex", {
                        "text-danger": item.isRestricted,
                      })}
                      key={item.dateTime}
                    >
                      <p>{item.dateTime}</p>
                    </div>
                  ),
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={onClose}>
                <Trans>Prekliči ustvarjanje</Trans>
              </Button>
              <Button
                color="primary"
                onPress={async () => {
                  try {
                    const response = await api.appointments.usePostAppointment({
                      ...dataForPost,
                      throwIfRestricted: false,
                    });

                    useAppointmentModalStore.setState({
                      lastCreatedAppointmentId:
                        response?.created?.[0]?.appointmentId ?? null,
                    });

                    addToast({
                      title: t`Uspešno ste ustvarili termin`,
                      color: "success",
                    });

                    useCalendarStore.getState().closeModals();
                  } catch (e) {
                    addToast({
                      title: t`Napaka pri ustvarjanju termina`,
                      color: "danger",
                    });
                  } finally {
                    onClose();
                  }
                }}
              >
                <Trans>Ustvari veljavne termine</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CalendarModalWrapper;
